import { Form, message } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../nestserver";
import { uploadDoc } from "../../../../services/upload.service";
import ComposeMessageForm from "./modals/ComposeMessageForm";

const AnnouncementCompose = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ── Core state ──────────────────────────────────
  const [classId, setClassId] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [subject, setSubject] = useState("");
  const [messageText, setMessageText] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [targetingType, setTargetingType] = useState("selected");

  // ── Scheduling ───────────────────────────────────
  const [sendType, setSendType] = useState("now");
  const [scheduledDate, setScheduledDate] = useState(null);
  const [recurrenceConfig, setRecurrenceConfig] = useState({
    type: "daily",
    at_time: "09:00",
    days_of_week: [1, 2, 3, 4, 5],
    timezone: "Asia/Kolkata",
  });

  // ── Templates (read-only, for the compose dropdown) ──
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // ────────────────────────────────────────────────
  // Data fetching
  // ────────────────────────────────────────────────
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/teachers/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const fetchStudentData = async () => {
    if (!classId) return [];
    const res = await axios.get(serverUrl + "/students/class/" + classId, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classDetails = useQuery(["class", classId], fetchStudentData, {
    enabled: !!classId,
  });

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(
        serverUrl + "/personal-messages/announcement-meta",
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      setTemplates(res.data?.result?.templates || []);
    } catch (err) {
      console.error("Failed to fetch templates", err);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // ────────────────────────────────────────────────
  // Student / parent selection
  // ────────────────────────────────────────────────
  const handleClassChange = (value) => {
    setClassId(value);
    setSelectedStudents([]);
    setSelectedParents([]);
  };

  const handleTargetingTypeChange = (value) => {
    setTargetingType(value);
    if (value === "all") {
      setClassId(null);
      setSelectedStudents([]);
      setSelectedParents([]);
    }
  };

  const handleSelectAllStudents = () => {
    if (!classDetails?.data) return;
    const studentsWithParents = classDetails.data.filter((s) => s.parent);
    const studentsWithoutParents = classDetails.data.filter((s) => !s.parent);

    if (studentsWithoutParents.length > 0) {
      message.warning(
        `${studentsWithoutParents.length} student(s) skipped (no parent assigned)`,
      );
    }

    setSelectedStudents(studentsWithParents);

    const uniqueParents = [];
    const parentIds = new Set();
    studentsWithParents.forEach((student) => {
      if (!parentIds.has(student.parent._id)) {
        parentIds.add(student.parent._id);
        uniqueParents.push({
          ...student.parent,
          student_name: `${student.first_name} ${student.last_name}`,
          role: student.parent.role || "Parent",
        });
      }
    });
    setSelectedParents(uniqueParents);
  };

  const handleStudentMultiChange = (values) => {
    const newStudents = values.map((id) =>
      classDetails?.data?.find((s) => s._id === id),
    );
    setSelectedStudents(newStudents);

    const uniqueParents = [];
    const parentIds = new Set();
    newStudents.forEach((student) => {
      if (student?.parent && !parentIds.has(student.parent._id)) {
        parentIds.add(student.parent._id);
        uniqueParents.push({
          ...student.parent,
          student_name: `${student.first_name} ${student.last_name}`,
          role: student.parent.role || "Parent",
        });
      }
    });
    setSelectedParents(uniqueParents);
  };

  const removeParent = (parentId) => {
    setSelectedParents(selectedParents.filter((p) => p._id !== parentId));
    setSelectedStudents(
      selectedStudents.filter((s) => s.parent?._id !== parentId),
    );
  };

  // ────────────────────────────────────────────────
  // File handling
  // ────────────────────────────────────────────────
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files?.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "audio/mpeg",
      "audio/mp3",
      "video/mp4",
      "audio/wav",
      "audio/webm",
    ];

    const maxTotalFiles = 15;
    const responses = [];

    try {
      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          message.error(`Invalid file type for ${file.name}`);
          continue;
        }

        let folder = "images";
        if (file.type === "application/pdf") {
          folder = "pdf";
        } else if (file.type.startsWith("video/")) {
          folder = "video";
        } else if (file.type.startsWith("audio/")) {
          folder = "audio";
        }

        const res = await uploadDoc({ file }, folder);

        let mediaType = "IMAGE";
        if (file.type === "application/pdf") mediaType = "PDF";
        else if (file.type.startsWith("video/")) mediaType = "VIDEO";
        else if (file.type.startsWith("audio/")) {
          const isVoice =
            file.type === "audio/wav" || file.type === "audio/webm";
          mediaType = isVoice ? "VOICE" : "AUDIO";
        }

        responses.push({ ...res, mediaType });
      }

      message.success("Files uploaded successfully");
      const newDocs = responses.reduce((acc, item) => {
        if (item?.files?.name && item?.id) {
          acc.push({
            name: item?.files?.name,
            id: item?.id,
            type: item?.mediaType,
          });
        }
        return acc;
      }, []);
      setUploadedFiles((prev) =>
        [...(prev || []), ...newDocs].slice(0, maxTotalFiles),
      );
    } catch (error) {
      console.error(error);
      message.error("File upload failed");
    }
  };

  const onRemoveFile = (index) => {
    setUploadedFiles((old) => old.filter((_, i) => i !== index));
    message.success("File removed");
  };

  // ────────────────────────────────────────────────
  // Send message
  // ────────────────────────────────────────────────
  const handleSendTypeChange = (e) => setSendType(e.target.value);

  const handleSendMessage = async () => {
    if (isSending) return;

    if (!subject.trim()) {
      message.error("Please enter a subject");
      return;
    }
    if (!messageText.trim()) {
      message.error("Please enter a message");
      return;
    }
    if (targetingType === "selected" && selectedParents.length === 0) {
      message.error("Please select at least one student/parent");
      return;
    }

    const payload = {
      subject,
      message: messageText,
      recipient_ids:
        targetingType === "all" ? [] : selectedParents.map((p) => p._id),
      class_ids: targetingType === "all" ? [] : (classId ? [classId] : []),
      media: uploadedFiles?.length > 0 ? uploadedFiles : [],
      template_id: selectedTemplate,
      send_to_all: targetingType === "all",
    };

    if (sendType === "scheduled" && scheduledDate) {
      payload.scheduled_at = scheduledDate.toISOString();
    }

    setIsSending(true);
    try {
      if (sendType === "recurring") {
        const recurringPayload = {
          ...payload,
          recipient_type: targetingType,
          template_id: selectedTemplate || undefined,
          recurrence: recurrenceConfig,
          is_active: true,
          timezone: recurrenceConfig.timezone,
        };
        await axios.post(serverUrl + "/message-schedules", recurringPayload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        message.success("Recurring schedule created successfully");
      } else {
        await axios.post(serverUrl + "/personal-messages", payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        message.success("Message sent successfully");
      }

      resetForm();
      queryClient.invalidateQueries(["personal-messages", "sent"]);
    } catch (err) {
      console.error(err);
      message.error("Failed to process message");
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setSubject("");
    setMessageText("");
    setUploadedFiles([]);
    setSelectedStudents([]);
    setSelectedParents([]);
    setClassId(null);
    setSendType("now");
    setScheduledDate(null);
    setSelectedTemplate(null);
    form.resetFields();
  };

  useEffect(() => {
    if (uploadedFiles?.length > 0 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [uploadedFiles]);

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161", overflowY: "auto" }}
        className="bg-white drop-shadow-md p-8 w-full screen-height"
      >
        <h2 className="text-xl font-bold m-0 text-gray-800 mb-6">
          Post Announcement
        </h2>

        <div style={{ maxWidth: 760 }}>
          <ComposeMessageForm
            form={form}
            inputRef={inputRef}
            isEditMode={false}
            classes={classes?.data || []}
            classId={classId}
            onClassChange={handleClassChange}
            classDetails={classDetails}
            selectedStudents={selectedStudents}
            selectedParents={selectedParents}
            onSelectAll={handleSelectAllStudents}
            onRemoveParent={removeParent}
            onStudentMultiChange={handleStudentMultiChange}
            subject={subject}
            onSubjectChange={(e) => setSubject(e.target.value)}
            messageText={messageText}
            onMessageChange={(e) => setMessageText(e.target.value)}
            uploadedFiles={uploadedFiles}
            onRemoveFile={onRemoveFile}
            onFileSelect={handleFileSelect}
            sendType={sendType}
            onSendTypeChange={handleSendTypeChange}
            scheduledDate={scheduledDate}
            onScheduledDate={(val) => setScheduledDate(val)}
            recurrenceConfig={recurrenceConfig}
            onRecurrenceChange={(partial) =>
              setRecurrenceConfig((prev) => ({ ...prev, ...partial }))
            }
            templates={templates}
            selectedTemplate={selectedTemplate}
            onTemplateChange={(val, template) => {
              if (template) {
                setSubject(template.subject);
                setMessageText(template.content || template.message);
              }
              setSelectedTemplate(val);
            }}
            onInsertTag={(tag) =>
              setMessageText((prev) => prev + (prev ? " " : "") + tag)
            }
            onSend={handleSendMessage}
            onResetForm={resetForm}
            targetingType={targetingType}
            onTargetingTypeChange={handleTargetingTypeChange}
            isSending={isSending}
          />
        </div>
      </div>
    </div>
  );
};

export default AnnouncementCompose;
